import { Fragment, StrictMode, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { MutationCache, QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';

import { ApiError } from './shared/api/client';
import { routeTree } from './routeTree.gen';
import { setupServiceWorker } from './shared/pwa/register-sw';
import { setupChunkRecovery } from './shared/pwa/chunk-recovery';
import { reportError, setupErrorReporter } from './shared/lib/error-reporter';
import { setupKeyboardInset } from './shared/lib/keyboard-inset';
import { setupOfflineCache } from './shared/lib/query-persist';
import { PwaUpdateBanner } from './shared/pwa/pwa-update-banner';
import { ThemedToaster } from './app/themed-toaster';
import { RoutePending } from './shared/ui/route-pending';
import { initLocale } from './shared/i18n/boot';
import { useLocale } from './shared/i18n/react';
import { RouteError } from './shared/ui/route-error';
import { UpgradePromptHost } from './widgets/billing/upgrade-prompt';
import { createMutationErrorHandler } from './app/mutation-error-handler';
import './app/styles/globals.css';

// Service worker registration — production only (vite-plugin-pwa
// `devOptions.enabled=false`).  The SW caches static assets for fast
// warm starts and is the foundation for push notifications + offline.
if (import.meta.env.PROD) {
  setupServiceWorker();
  // Self-heal a stale-chunk load after a deploy (see setupChunkRecovery). PROD
  // only — in dev, hashed chunks don't rotate and this could fight HMR.
  setupChunkRecovery();
}

// Self-hosted crash reporting: uncaught errors + unhandled rejections →
// POST /api/client-errors (deduped, rate-limited, batched). Error-boundary
// catches arrive via createRoot's onCaughtError below. See error-reporter.ts.
setupErrorReporter();

// Track the on-screen keyboard height (→ CSS var --kb-inset) so mobile
// bottom-sheets can sit above the iOS keyboard instead of behind it. Runs in
// all envs (harmless on desktop — the inset stays 0).
setupKeyboardInset();

// A file dropped anywhere that isn't a drop target must never leave the app
// (see setupFileDropGuard).
setupFileDropGuard();

// Global mutation error handler — ONE toast per failed mutation. The rule
// (meta opt-out, local onError/catch toasts win, 402 → upgrade toast,
// FREE_TIER → upgrade dialog) lives in app/mutation-error-handler.ts.
const mutationCache = new MutationCache({
  onError: createMutationErrorHandler({
    openBilling: () => void router.navigate({ to: '/account/billing' }),
  }),
});

/**
 * The browser default for an unhandled file drop is to open the file in the
 * tab — navigating the SPA away and losing any draft (Safari does exactly
 * that). Bubble phase, so real targets (composers, the editor, FileDropZone)
 * act first: one that accepts the drag has already called preventDefault on
 * dragover, and everywhere else shows "no drop" instead of a copy cursor.
 * Only OS file drags are touched — text drags between inputs keep their
 * native behaviour, and a visible native file input still takes its drop.
 */
function setupFileDropGuard(): void {
  const isFileDrag = (e: DragEvent) =>
    !!e.dataTransfer && Array.from(e.dataTransfer.types).includes('Files');
  const isFileInput = (target: EventTarget | null) =>
    target instanceof HTMLInputElement && target.type === 'file';
  window.addEventListener('dragover', (e) => {
    if (!isFileDrag(e) || e.defaultPrevented || isFileInput(e.target)) return;
    e.preventDefault();
    e.dataTransfer!.dropEffect = 'none';
  });
  window.addEventListener('drop', (e) => {
    if (!isFileDrag(e) || isFileInput(e.target)) return;
    e.preventDefault();
  });
}

const queryClient = new QueryClient({
  mutationCache,
  defaultOptions: {
    queries: {
      staleTime: 60 * 1000,
      gcTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
      // Never retry 4xx (incl. 429) — retrying a rate-limited request just
      // amplifies the storm. Retry genuinely transient errors once.
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 1;
      },
    },
  },
});

// Offline cache: My Work, Inbox and attention persist to IndexedDB (allowlist
// only, per user, wiped on sign-out), so a cold launch of the installed app
// shows them at once and airplane mode keeps the last data. See query-persist.
const persistOptions = setupOfflineCache(queryClient);

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // Route chunks are lazy now (autoCodeSplitting). A layout-reserving skeleton
  // covers the fetch gap on cold navigations; `defaultPendingMs` keeps it from
  // flashing on fast / hover-preloaded loads. `defaultErrorComponent` makes a
  // stale-deploy chunk miss self-heal instead of dead-ending.
  defaultPendingComponent: RoutePending,
  defaultPendingMs: 150,
  defaultPendingMinMs: 300,
  defaultErrorComponent: RouteError,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Missing #root');

/**
 * A language switch remounts the app below the providers (the query cache and
 * the router's location survive), so every string — including ones computed
 * outside React with `tr()` — re-renders in the new language without a reload.
 */
function LocaleBoundary({ children }: { children: ReactNode }) {
  const locale = useLocale();
  return <Fragment key={locale}>{children}</Fragment>;
}

function renderApp() {
  createRoot(rootEl!, {
    // Errors an error boundary (incl. the router's) caught and rendered a fallback
    // for — they never reach window 'error', so report them here. Keep React's
    // default console output so devtools still show them.
    onCaughtError: (error, info) => {
      reportError(error, 'boundary');
      console.error(error, info.componentStack);
    },
  }).render(
    <StrictMode>
      <PersistQueryClientProvider client={queryClient} persistOptions={persistOptions}>
        <LocaleBoundary>
          <RouterProvider router={router} />
          <PwaUpdateBanner />
          <UpgradePromptHost />
        </LocaleBoundary>
        <ThemedToaster />
      </PersistQueryClientProvider>
    </StrictMode>,
  );
  window.__pjMounted = true;
}

// The UI language (cached choice → browser language → English) is loaded
// before the first paint, so a Russian user never sees an English flash.
// initLocale never rejects; a failed chunk load just stays in English.
void initLocale().then(renderApp);

// Signal the index.html boot watchdog that the app JS booted. Reaching this
// line means the entry bundle loaded and React mounted — so the watchdog stands
// down instead of hard-recovering. A failed/stale-chunk boot never gets here,
// so the watchdog fires and self-heals. See index.html.
declare global {
  interface Window {
    __pjMounted?: boolean;
  }
}
